import { ReactNode, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useLogout, usePermissionGuard } from '../../hooks/shared/auth'
import Sidebar from './Sidebar'
import ReAuthBanner from '../accounts/ReAuthBanner'
import MaintenanceBanner from '../ui/MaintenanceBanner'
import SiteNoticeBanner from '../ui/SiteNoticeBanner'
import { BalanceBadge } from '../credits'
import { FreePlanUpgradeTopbarCta } from '../credits/FreePlanUpgradeTopbarCta'
import { NotificationBell } from '../notifications/NotificationBell'
import { OnboardingCompletionModal } from '../onboarding/OnboardingCompletionModal'
import { useOnboardingGuidedHydration } from '../../hooks/shared/onboarding/useOnboardingGuidedHydration'
import { useOnboardingCompletionEffects } from '../../hooks/shared/onboarding/useOnboardingCompletionEffects'
import LanguageSelector from './LanguageSelector'
import { MODULE_REGISTRY, getAllRoutesForModule } from '../../config/moduleRegistry'
import { useSidebarState } from '../../hooks/shared/useSidebarState'
import { ChevronDown, Coins, Menu, PanelLeft } from 'lucide-react'
import { useCreditBalance } from '../../hooks/credits/useCreditBalance'

// ─── User menu ────────────────────────────────────────────────────────────────

/**
 * Self-contained user dropdown — owns its own state, hooks, and handlers
 * so MainLayout stays focused on layout concerns only.
 */
function UserMenu() {
  const { user } = useAuthStore()
  const logoutMutation = useLogout()
  const { t } = useTranslation(['dashboard', 'common', 'profile', 'credits'])
  const { data: balance } = useCreditBalance()
  const [open, setOpen] = useState(false)

  if (!user) return null

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  const roleLabel =
    user.role === 'admin' ? t('dashboard:header.roles.admin') : t('dashboard:header.roles.user')

  const total = balance?.total ?? 0

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="flex items-center space-x-1 sm:space-x-2 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <div className="w-8 h-8 bg-[#212121] rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
          {user.first_name?.charAt(0)}
          {user.last_name?.charAt(0)}
        </div>
        <ChevronDown className="hidden sm:block w-4 h-4 text-gray-500" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 min-w-56 max-w-xs bg-white rounded-md shadow-lg py-1 z-50 border">
          <div className="px-4 py-2 text-sm text-gray-700 border-b">
            <div className="font-medium break-all">{user.email}</div>
            <div className="text-xs text-gray-500">{roleLabel}</div>
          </div>

          {/* Balance — mobile only: simple link to /credits, no nested popup */}
          <Link
            to="/credits"
            onClick={() => setOpen(false)}
            className="sm:hidden flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-b"
          >
            <Coins size={16} className="text-yellow-500 flex-shrink-0" />
            <span>{total.toLocaleString()} {t('credits:unit')}</span>
          </Link>

          {/* Language — mobile only */}
          <div className="sm:hidden px-4 py-2 border-b">
            <LanguageSelector />
          </div>

          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {t('profile:pageTitle')}
          </Link>

          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            {t('common:nav.logout')}
          </button>
        </div>
      )}

      {/* Click-outside overlay */}
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  )
}

// ─── Layout ───────────────────────────────────────────────────────────────────

interface Props {
  children: ReactNode
}

export default function MainLayout({ children }: Props) {
  const location = useLocation()
  const { t } = useTranslation(['dashboard', 'profile', 'credits'])
  const { isCollapsed, isMobileOpen, toggleCollapsed, openMobile, closeMobile } = useSidebarState()
  const topChromeRef = useRef<HTMLDivElement>(null)
  useOnboardingGuidedHydration()
  useOnboardingCompletionEffects()

  // Refresh permissions on route navigation
  usePermissionGuard()

  // Keep toast position below all top chrome (maintenance/legacy banners + header + re-auth).
  useEffect(() => {
    const el = topChromeRef.current
    if (!el) return

    const updateOffset = () => {
      document.documentElement.style.setProperty('--app-top-offset', `${el.offsetHeight}px`)
    }

    updateOffset()
    const observer = new ResizeObserver(updateOffset)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Expose sidebar width for fixed UI (e.g. offer-management bulk bar) on desktop.
  useEffect(() => {
    const updateSidebarWidth = () => {
      const isLg = window.matchMedia('(min-width: 1024px)').matches
      const width = isLg ? (isCollapsed ? '4rem' : '16rem') : '0px'
      document.documentElement.style.setProperty('--app-sidebar-width', width)
    }

    updateSidebarWidth()
    window.addEventListener('resize', updateSidebarWidth)
    return () => window.removeEventListener('resize', updateSidebarWidth)
  }, [isCollapsed])

  const getPageTitle = (pathname: string): string => {
    const module =
      MODULE_REGISTRY.find((m) => getAllRoutesForModule(m).includes(pathname)) ??
      MODULE_REGISTRY.filter(
        (m) => pathname === m.route || pathname.startsWith(`${m.route}/`),
      ).sort((a, b) => b.route.length - a.route.length)[0]
    if (module) return t(`dashboard:${module.name}`)

    const specials: Record<string, string> = {
      '/profile': t('profile:pageTitle'),
      '/profile/ai-config': t('profile:aiConfig.pageTitle'),
      '/users-analytics': t('dashboard:sidebar.adminLinks.usersAnalytics'),
      '/admin/users': t('dashboard:sidebar.adminLinks.manageUsers'),
      '/admin/ai-prompts': t('dashboard:sidebar.adminLinks.aiConfig'),
      '/admin/opisomat-config': t('dashboard:sidebar.adminLinks.opisomatConfig'),
      '/admin/credits': t('credits:sidebar.adminCredits'),
      '/admin/referrals': t('dashboard:sidebar.adminLinks.referrals'),
      '/admin/accounting': t('dashboard:sidebar.adminLinks.accounting'),
      '/admin/site-notice': t('dashboard:sidebar.adminLinks.siteNotice'),
    }
    return specials[pathname] ?? 'Ofertator'
  }

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Mobile backdrop */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-hidden="true"
          onClick={closeMobile}
        />
      )}

      <Sidebar
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleCollapsed}
        isMobileOpen={isMobileOpen}
        onCloseMobile={closeMobile}
      />

      <div className="flex flex-1 flex-col min-w-0">
        <div ref={topChromeRef}>
        <MaintenanceBanner />
        <SiteNoticeBanner context="authenticated" />

        <header className="h-16 bg-[#0d0d0d] border-b border-l border-[#212121] flex items-center justify-between pl-2 pr-6">
          <div className="flex items-center space-x-2">
            {/* Mobile: open sidebar drawer */}
            <button
              onClick={openMobile}
              aria-label="Open menu"
              className="lg:hidden flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-white hover:bg-[#1f1f1f] transition"
            >
              <Menu className="w-5 h-5" />
            </button>
            {/* Desktop: collapse / expand sidebar */}
            <button
              onClick={toggleCollapsed}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              className="hidden lg:flex items-center justify-center w-8 h-8 rounded text-gray-400 hover:text-white hover:bg-[#1f1f1f] transition"
            >
              <PanelLeft
                className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
              />
            </button>
            <h1 className="text-lg font-semibold font-switzer text-white truncate">
              {getPageTitle(location.pathname)}
            </h1>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <FreePlanUpgradeTopbarCta />
            <div className="hidden sm:block">
              <LanguageSelector />
            </div>
            <div className="hidden sm:block">
              <BalanceBadge />
            </div>
            <NotificationBell />
            <UserMenu />
          </div>
        </header>

        <ReAuthBanner />
        </div>
        <OnboardingCompletionModal />
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-8 pb-24">{children}</main>
      </div>
    </div>
  )
}
